import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { verificationAPI } from '../api';
import { toast } from 'sonner';
import { 
  ShieldCheck, 
  Mail, 
  CheckCircle2, 
  Clock, 
  XCircle,
  Loader2,
  ArrowLeft,
  Hash
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { VerificationStatusResponse } from '../types';

const VerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState<VerificationStatusResponse | null>(null);
  
  // Tab state
  const [method, setMethod] = useState<'email' | 'npi'>('email');
  
  // Work email form state
  const [workEmail, setWorkEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState<'check' | 'form' | 'code' | 'verified'>('check');
  const [submitting, setSubmitting] = useState(false);
  const [codeSentTo, setCodeSentTo] = useState('');

  // NPI form state
  const [npiNumber, setNpiNumber] = useState('');

  useEffect(() => {
    loadVerificationStatus();
  }, []);

  const loadVerificationStatus = async () => {
    try {
      setLoading(true);
      const verStatus = await verificationAPI.getStatus();
      setVerificationStatus(verStatus);
      
      if (verStatus.status === 'verified' || verStatus.status === 'verified_provisional' as any) {
        setStep('verified');
      } else {
        setStep('form');
      }
    } catch (error) {
      console.error('Error loading verification status:', error);
      setStep('form');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async () => {
    if (!workEmail.trim()) {
      toast.error('Please enter your work email address');
      return;
    }
    setSubmitting(true);
    try {
      const response = await verificationAPI.sendWorkEmailCode({ work_email: workEmail });
      setCodeSentTo(response.email_sent_to);
      setStep('code');
      toast.success('Verification code sent!');
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to send verification code';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmCode = async () => {
    if (!verificationCode.trim() || verificationCode.length < 6) {
      toast.error('Please enter your 6-digit verification code');
      return;
    }
    setSubmitting(true);
    try {
      const response = await verificationAPI.confirmWorkEmailCode({ code: verificationCode });
      if (response.status === 'verified') {
        setStep('verified');
        toast.success(response.message);
        const newStatus = await verificationAPI.getStatus();
        setVerificationStatus(newStatus);
      }
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Invalid verification code';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitNPI = async () => {
    if (!npiNumber.trim() || npiNumber.length !== 10) {
      toast.error('Please enter a valid 10-digit NPI number');
      return;
    }
    setSubmitting(true);
    try {
      const response = await verificationAPI.submitNPI(npiNumber);
      toast.success(response.message);
      const newStatus = await verificationAPI.getStatus();
      setVerificationStatus(newStatus);
      if (newStatus.status === 'verified' || (newStatus.status as string) === 'verified_provisional') {
        setStep('verified');
      }
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'object' ? detail?.message : (detail || 'Failed to submit NPI');
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = () => {
    if (!verificationStatus) return <ShieldCheck className="h-8 w-8 text-gray-400" />;
    switch (verificationStatus.status) {
      case 'verified':
        return <CheckCircle2 className="h-8 w-8 text-emerald-500" />;
      case 'pending':
        return <Clock className="h-8 w-8 text-amber-500" />;
      case 'rejected':
        return <XCircle className="h-8 w-8 text-red-500" />;
      default:
        if ((verificationStatus.status as string) === 'verified_provisional')
          return <CheckCircle2 className="h-8 w-8 text-sky-500" />;
        return <ShieldCheck className="h-8 w-8 text-gray-400" />;
    }
  };

  const getStatusBadge = () => {
    if (!verificationStatus) return <Badge className="bg-gray-100 text-gray-700 border-0">Not Verified</Badge>;
    const s = verificationStatus.status as string;
    if (s === 'verified') return <Badge className="bg-emerald-100 text-emerald-700 border-0">Verified</Badge>;
    if (s === 'verified_provisional') return <Badge className="bg-sky-100 text-sky-700 border-0">Verified (Provisional)</Badge>;
    if (s === 'pending') return <Badge className="bg-amber-100 text-amber-700 border-0">Pending Review</Badge>;
    if (s === 'rejected') return <Badge className="bg-red-100 text-red-700 border-0">Rejected</Badge>;
    return <Badge className="bg-gray-100 text-gray-700 border-0">Not Verified</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const isVerified = step === 'verified';

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6" data-testid="back-button">
        <ArrowLeft className="h-4 w-4 mr-2" />Back
      </Button>

      <Card data-testid="verification-card">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">{getStatusIcon()}</div>
          <CardTitle className="text-2xl">Clinician Verification</CardTitle>
          <CardDescription>
            Verify your credentials to post in community discussions and unlock additional digest profiles
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Status display */}
          <div className="flex items-center justify-center gap-2">
            <span className="text-sm text-gray-600">Current Status:</span>
            {getStatusBadge()}
          </div>

          {/* Already verified */}
          {isVerified && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center" data-testid="verified-message">
              <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-emerald-800 mb-2">You're Verified!</h3>
              <p className="text-emerald-700">
                Your clinician credentials have been verified. You can now post in community discussions
                {verificationStatus?.method === 'npi' ? ' (via NPI)' : ' (via work email)'}.
              </p>
              {verificationStatus?.verified_at && (
                <p className="text-sm text-emerald-600 mt-4">
                  Verified on {new Date(verificationStatus.verified_at).toLocaleDateString()}
                </p>
              )}
            </div>
          )}

          {/* Pending state */}
          {!isVerified && verificationStatus?.status === 'pending' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
              <Clock className="h-8 w-8 text-amber-500 mx-auto mb-2" />
              <h4 className="font-semibold text-amber-800">Verification Pending</h4>
              <p className="text-sm text-amber-700 mt-1">
                Your {verificationStatus.method === 'npi' ? 'NPI' : 'verification'} is being reviewed. You'll be notified when approved.
              </p>
            </div>
          )}

          {/* Method selection tabs */}
          {!isVerified && verificationStatus?.status !== 'pending' && (
            <>
              <div className="flex border rounded-lg overflow-hidden">
                <button
                  onClick={() => setMethod('email')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors ${
                    method === 'email'
                      ? 'bg-sky-50 text-sky-700 border-b-2 border-sky-500'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Mail className="h-4 w-4" /> Work Email
                </button>
                <button
                  onClick={() => setMethod('npi')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors ${
                    method === 'npi'
                      ? 'bg-sky-50 text-sky-700 border-b-2 border-sky-500'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Hash className="h-4 w-4" /> NPI Number
                </button>
              </div>

              {/* Work Email flow */}
              {method === 'email' && step === 'form' && (
                <div className="space-y-4" data-testid="email-step">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Mail className="h-5 w-5 text-gray-500 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-gray-900">Work Email Verification</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          We'll send a verification code to your institutional or work email address.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="workEmail">Work Email Address</Label>
                    <Input id="workEmail" type="email" placeholder="your.name@institution.edu"
                      value={workEmail} onChange={(e) => setWorkEmail(e.target.value)}
                      disabled={submitting} data-testid="work-email-input" />
                    <p className="text-xs text-gray-500">Enter your institutional, hospital, or university email</p>
                  </div>
                  <Button onClick={handleSendCode} disabled={submitting || !workEmail.trim()} className="w-full" data-testid="send-code-button">
                    {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</> : <><Mail className="h-4 w-4 mr-2" />Send Verification Code</>}
                  </Button>
                </div>
              )}

              {/* Code entry step */}
              {method === 'email' && step === 'code' && (
                <div className="space-y-4" data-testid="code-step">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                    <p className="text-blue-700">Verification code sent to <strong>{codeSentTo}</strong></p>
                    <p className="text-sm text-blue-600 mt-1">Enter the 6-digit code below</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="verificationCode">Verification Code</Label>
                    <Input id="verificationCode" type="text" placeholder="Enter 6-digit code"
                      value={verificationCode} onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      disabled={submitting} className="text-center text-2xl tracking-widest font-mono" maxLength={6}
                      data-testid="verification-code-input" />
                  </div>
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => setStep('form')} disabled={submitting} className="flex-1" data-testid="back-to-email-button">Change Email</Button>
                    <Button onClick={handleConfirmCode} disabled={submitting || verificationCode.length < 6} className="flex-1" data-testid="confirm-code-button">
                      {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Verifying...</> : <><CheckCircle2 className="h-4 w-4 mr-2" />Verify</>}
                    </Button>
                  </div>
                  <Button variant="link" onClick={handleSendCode} disabled={submitting} className="w-full text-sm" data-testid="resend-code-button">
                    Didn't receive the code? Send again
                  </Button>
                </div>
              )}

              {/* NPI flow */}
              {method === 'npi' && step !== 'code' && (
                <div className="space-y-4" data-testid="npi-step">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Hash className="h-5 w-5 text-gray-500 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-gray-900">NPI Verification</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          Enter your 10-digit National Provider Identifier (NPI) number.
                          Your NPI will be validated and stored securely.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="npiNumber">NPI Number</Label>
                    <Input id="npiNumber" type="text" placeholder="1234567890"
                      value={npiNumber} onChange={(e) => setNpiNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      disabled={submitting} className="font-mono tracking-wider" maxLength={10}
                      data-testid="npi-input" />
                    <p className="text-xs text-gray-500">Your 10-digit NPI from the NPPES registry</p>
                  </div>
                  <Button onClick={handleSubmitNPI} disabled={submitting || npiNumber.length !== 10} className="w-full" data-testid="submit-npi-button">
                    {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting...</> : <><ShieldCheck className="h-4 w-4 mr-2" />Submit NPI for Verification</>}
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Info section */}
          <div className="border-t pt-4">
            <h4 className="font-medium text-gray-900 mb-2">Why verify?</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• <strong>Required</strong> to post, comment, and react in Community discussions</li>
              <li>• Unlock 3 digest profiles (vs 1 for unverified free users)</li>
              <li>• Display a Verified Clinician badge on your comments</li>
              <li>• Help establish trust in the professional community</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerificationPage;
